import { useCallback, useState, useTransition } from 'react';

import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { useRouter as useAppRouter, useParams } from 'next/navigation';
import { useShallow } from 'zustand/react/shallow';

import { normalizeEmail } from 'services/normalizeEmail';
import { getSafeRedirectPath } from 'services/safeRedirect';

import { auth } from 'clients/firebase';

import { useAuthGateStore } from 'stores/authGateStore';

import { useClearUser, useFetchUser } from './user';

const provider = new GoogleAuthProvider();
provider.setCustomParameters({
  prompt: 'select_account',
});

export class NonVerifiedEmailError extends Error {
  name: string = 'NonVerifiedEmailError';
  message: string =
    "This email address hasn't been verified yet, please check your inbox and follow the instructions to verify your email address";
}

export const useEmailSignIn = () => {
  const { refetch } = useFetchUser();

  const { push } = useAppRouter();
  const params = useParams<{ companySlug?: string }>();
  const companySlug = params?.companySlug;
  const [loading, setLoading] = useState(false);
  const [isPending, startTransition] = useTransition();
  const { setStatus: setAuthGateStatus, setMethod: setAuthGateMethod } =
    useAuthGateStore(
      useShallow(state => ({
        setStatus: state.setStatus,
        setMethod: state.setMethod,
      })),
    );

  const emailSignInHandler = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      try {
        setAuthGateMethod('email');
        setAuthGateStatus('checking');
        setLoading(true);
        const { user } = await signInWithEmailAndPassword(
          auth,
          normalizeEmail(email),
          password,
        );

        if (!user.emailVerified) {
          throw new NonVerifiedEmailError();
        }

        const idToken = await user.getIdToken();

        await fetch('/api/session/login', {
          method: 'POST',
          body: JSON.stringify({ idToken }),
        });

        await refetch();

        // `from` is read here rather than through useSearchParams() at render time:
        // this hook runs inside AuthProtection, and a render-time useSearchParams()
        // would exclude the whole login form from static prerendering (Next.js's
        // missing-suspense-with-csr-bailout). It is only needed in this handler.
        const from = getSafeRedirectPath(
          new URLSearchParams(window.location.search).get('from'),
        );

        // Wrapped in startTransition so `pending` (loading || isPending) stays true
        // until the navigation itself commits, closing the flash-of-interactive-form
        // window between the auth call resolving and the destination page painting.
        startTransition(() => {
          setAuthGateStatus('redirecting');

          if (from) {
            push(from);
          } else if (companySlug) {
            push(`/${companySlug}`);
          }
          // On the top-level /login route there is no companySlug and no `from`;
          // `refetch()` above updates the user, and `LoginRedirect`
          // (useRedirectToUserCompany) picks the destination from the profile.
        });
      } catch (error) {
        setAuthGateMethod(null);
        setAuthGateStatus('error');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [push, companySlug, refetch, setAuthGateStatus, setAuthGateMethod],
  );

  return {
    loading,
    pending: loading || isPending,
    emailSignInHandler,
  };
};

export const useEmailSignUp = () => {
  const { push } = useAppRouter();
  const [loading, setLoading] = useState(false);
  const [isPending, startTransition] = useTransition();
  const setAuthGateStatus = useAuthGateStore(state => state.setStatus);
  const setAuthGateMethod = useAuthGateStore(state => state.setMethod);

  const emailSignUpHandler = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      try {
        setLoading(true);
        setAuthGateMethod('email');
        setAuthGateStatus('checking');

        const { user } = await createUserWithEmailAndPassword(
          auth,
          normalizeEmail(email),
          password,
        );

        const urlWithOutHash = window.location.href.split('#')[0];
        await sendEmailVerification(user, {
          url: urlWithOutHash,
          handleCodeInApp: false,
        });

        startTransition(() => {
          setAuthGateStatus('redirecting');
          push(`/unverified-user`);
        });
      } catch (error) {
        console.log(error);
        setAuthGateMethod(null);
        setAuthGateStatus('idle');
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [push, setAuthGateStatus, setAuthGateMethod],
  );
  return {
    loading,
    pending: loading || isPending,
    emailSignUpHandler,
  };
};

export const useGoogleSignButton = () => {
  const { refetch } = useFetchUser();
  const { setStatus: setAuthGateStatus, setMethod: setAuthGateMethod } =
    useAuthGateStore(
      useShallow(state => ({
        setStatus: state.setStatus,
        setMethod: state.setMethod,
      })),
    );

  const [loading, setLoading] = useState(false);

  const onGoogleButtonPressHandler = useCallback(async () => {
    try {
      setAuthGateMethod('google');
      setAuthGateStatus('checking');
      setLoading(true);

      const { user } = await signInWithPopup(auth, provider);

      const idToken = await user.getIdToken();

      await fetch('/api/session/login', {
        method: 'POST',
        body: JSON.stringify({ idToken }),
      });
      await refetch();
    } catch (error) {
      setAuthGateMethod(null);
      setAuthGateStatus('error');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [refetch, setAuthGateStatus, setAuthGateMethod]);

  return {
    loading,
    onGoogleButtonPressHandler,
  };
};

/**
 * Takes `companySlug` as an argument rather than reading it from `useParams()`: the
 * caller (`UserMenu`) already has it from `company.slug`, and `useParams()` can be
 * missing/still resolving, which previously sent the user to the literal path
 * `/undefined/login`.
 */
export const useLogout = (companySlug: string | undefined) => {
  const { clearUser } = useClearUser();
  const { push } = useAppRouter();

  const handleLogout = useCallback(async () => {
    await fetch('/api/session/logout', {
      method: 'POST',
    });

    clearUser();

    push(companySlug ? `/${companySlug}/login` : '/login');
  }, [clearUser, push, companySlug]);

  return {
    handleLogout,
  };
};

export const useForgotPassword = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const forgotPasswordHandler = useCallback(
    async ({ email }: { email: string }) => {
      try {
        setLoading(true);
        setSuccess(false);

        await sendPasswordResetEmail(auth, normalizeEmail(email), {
          url: window.location.origin,
          handleCodeInApp: false,
        });

        setSuccess(true);
      } catch (error) {
        console.log(error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    loading,
    success,
    forgotPasswordHandler,
  };
};
